import { Api } from "./api";
import { Dispatch } from "redux";
import axios, { AxiosResponse } from "axios";
import config from "../utils/config";
import { IUser } from "../interfaces/user";
import {
	pendingUserResponseAction,
	fetchAdminProductResponseAction,
	selectProductAction,
	fetchAdminReportRequestsAction,
	fetchAdminReportsAction,
	fetchEmailScheduleResponse,
	allUsersResponseAction,
	productStatisticsResponseAction
} from "../redux/actions/admin/adminActions";
import notification, { info, product } from "../utils/notification";
import { IApplicationState } from "../redux/reducers";
import { IProduct } from "../interfaces/products";
import { IEmailSchedule } from "../interfaces/admin";
import { getProductDetailResponseAction } from "../redux/actions/products/products";

const DEFAULT_REPORT_ORDER = {
	order_on: "request_date",
	order_by: "DESC"
};

export class AdminService extends Api {
	getAdminProducts(
		all: boolean = false,
		companyId?: string,
		resolve?: Function,
		reject?: Function,
		signal?: AbortSignal
	) {
		return async (dispatch: Dispatch<any>, getState: () => IApplicationState) => {
			try {
				const {
					admin: {
						products: { pagination }
					}
				} = getState();
				let params = companyId ? { company_id: companyId } : {};
				const requestConfig: any = {
					params: all ? { ...params } : {
						...params,
						offset: pagination.page,
						limit: pagination.size
					},
					...(signal && { signal })
				};

				if (all) {
					const response = await this.http.get(config.api.endpoints.admin.products, requestConfig);
					dispatch(
						fetchAdminProductResponseAction({
							pagination: response.data.meta,
							products: response.data
						})
					);
				} else {
					const response = await this.http.get(config.api.endpoints.admin.products, requestConfig);
					dispatch(
						fetchAdminProductResponseAction({
							pagination: response.data.meta,
							products: response.data.data
						})
					);
				}
			} catch (error) {
				// Don't show error if request was aborted
				const err = error as any;
				if (err?.name !== 'AbortError' && err?.code !== 'ERR_CANCELED') {
					this.handleError(error, "Product Error");
				}
				throw error;
			}
		};
	}

	// Anonymous Data Migration Methods
	async getAnonymousData(params: any, signal?: AbortSignal) {
		// Clean up params to avoid sending empty values
		const cleanParams: any = {};
		
		Object.keys(params).forEach(key => {
			const value = params[key];
			// Only include non-empty, non-null, non-undefined values
			if (value !== null && value !== undefined && value !== '') {
				cleanParams[key] = value;
			}
		});
		
		const requestConfig: any = {
			params: cleanParams,
			...(signal && { signal })
		};
		
		const response = await this.http.get(config.api.endpoints.admin.anonymousData, requestConfig);
		return response.data;
	}

	async getSchemaMapping(targetType: "products" | "ingredients") {
		const response = await this.http.get(`${config.api.endpoints.admin.schemaMapping}?target=${targetType}`);
		return response.data;
	}

	async previewMigration(data: { selected_ids: string[], target_table: string }) {
		const response = await this.http.post(config.api.endpoints.admin.previewMigration, data);
		return response.data;
	}

	async performMigration(targetType: "products" | "ingredients", data: { selected_ids: string[], additional_data: Record<string, any> }) {
		const endpoint = targetType === "products" 
			? config.api.endpoints.admin.migrateToProducts
			: config.api.endpoints.admin.migrateToIngredients;
		
		const response = await this.http.post(endpoint, data);
		return response.data;
	}

	getAdminReportRequests() {
		return async (dispatch: Dispatch<any>, getState: () => IApplicationState) => {
			try {
				const {
					admin: {
						requests: { pagination }
					}
				} = getState();
				const response = await this.http.get(config.api.endpoints.admin.requests, {
					params: {
						offset: pagination.page,
						limit: pagination.size,
						...DEFAULT_REPORT_ORDER
					}
				});

				dispatch(
					fetchAdminReportRequestsAction({
						pagination: response.data.meta,
						list: response.data.data
					})
				);
			} catch (error) {
				this.handleError(error, "Admin Report Requests");
			}
		};
	}

	getAdminReports() {
		return async (dispatch: Dispatch<any>, getState: () => IApplicationState) => {
			try {
				const {
					admin: {
						reports: { pagination }
					}
				} = getState();
				const response = await this.http.get(config.api.endpoints.admin.reports, {
					params: {
						offset: pagination.page,
						limit: pagination.size,
						...DEFAULT_REPORT_ORDER
					}
				});

				dispatch(
					fetchAdminReportsAction({
						pagination: response.data.meta,
						list: response.data.data
					})
				);
			} catch (error) {
				this.handleError(error, "Admin Reports");
			}
		};
	}

	// editAdminProduct(
	// 	productId: string,
	// 	productData: IProduct,
	// 	resolve?: Function,
	// 	reject?: Function
	// ) {
	// 	return async (dispatch: Dispatch<any>, getState: () => IApplicationState) => {
	// 		try {
	// 			const response = await this.http.put(config.api.endpoints.admin.editProduct(productId), productData);
	// 			dispatch(getProductDetailResponseAction(response.data));

	// 			product("Product updated successfully");
	// 		} catch (error) {
	// 			this.handleError(error, "Edit Product");
	// 		}
	// 	};
	// }
	editAdminProduct(
		productId: string,
		productData: IProduct,
		resolve?: Function,
		reject?: Function
	) {
		return async (dispatch: Dispatch<any>, getState: () => IApplicationState) => {
			try {
				if (!productId) {
					console.error("Product ID is missing in editAdminProduct");
					return;
				}
				const url = config.api.endpoints.admin.editProduct(productId);
				
				const response = await this.http.put(url, productData);
				dispatch(getProductDetailResponseAction(response.data));

				product("Product updated successfully");

				if (resolve) resolve(response.data);
			} catch (error) {
				console.error("Error in editAdminProduct:", error);
				this.handleError(error, "Edit Product");
				if (reject) reject(error);
			}
		};
	}

	getAllUsers(resolve?: Function, reject?: Function) {
		return async (dispatch: Dispatch<any>) => {
			try {
				const response = await this.http.get(config.api.endpoints.admin.users);
				dispatch(allUsersResponseAction(response.data));
			} catch (error) {
				this.handleError(error, "User requests");
				throw error;
			}
		};
	}

	getPendingUsers(resolve?: Function, reject?: Function) {
		return async (dispatch: Dispatch<any>) => {
			try {
				const response: AxiosResponse<IUser[]> = await this.http.get(
					config.api.endpoints.admin.users
				);

				dispatch(pendingUserResponseAction(response.data));
				if (resolve) {
					resolve(response);
				}
			} catch (error) {
				this.handleError(error, "Pending users");
				if (reject) reject(error);
			}
		};
	}

	updateUserById(id: string, userData: Partial<IUser>) {
		return async (dispatch: Dispatch<any>) => {
			try {
				const response = await this.http.put(config.api.endpoints.admin.editUser(id), userData);
			} catch (error) {
				this.handleError(error, "Update User");
			}
		};
	}

	approveOrRejectUser(userId: string, isApproved: boolean) {
		return async (dispatch: Dispatch<any>) => {
			try {
				const response: AxiosResponse<IUser> = await this.http.post(
					isApproved ? config.api.endpoints.admin.approve : config.api.endpoints.admin.reject,
					{
						user_id: userId
					}
				);
				info({
					title: "Approve User",
					description: `User ${response.data.first_name} has been ${isApproved ? "approved" : "rejected"
						} successfully`
				});
			} catch (error) {
				this.handleError(error);
			}
		};
	}
	modifyUser(userId: string, data: any) {
		return async (dispatch: Dispatch<any>) => {
			// Only allow these fields to be sent
			const allowedFields = [
				"first_name",
				"last_name",
				"email",
				"phone_number",
				"job_title",
				"city",
				"state",
				"country",
				"company_type",
				"role",
				"disable",
				"is_active",
				"company_name"
			];

			// Build sanitized payload
			const payload: Record<string, any> = {};
			allowedFields.forEach((key) => {
				if (key in data) {
					const value = data[key];

					// Skip undefined values
					if (value === undefined) return;

					// Allow empty strings for company_name (to allow clearing)
					if (key === "company_name" && value !== null) {
						payload[key] = value;
					} else if (value !== "" && value !== null) {
						payload[key] = value;
					}
				}
			});

			try {
				const response = await this.http.patch(
					config.api.endpoints.admin.modifyUser(userId),
					payload
				);

				info({
					title: "User modify",
					description: "User successfully updated."
				});

				return response.data;
			} catch (error) {
				console.error("❌ modifyUser failed:", error);
				throw error;
			}
		};
	}

	// 	modifyUser(userId: string, data: any) {
	//     return async (dispatch: Dispatch<any>) => {
	//         // Only include allowed fields
	//         const allowedFields = [
	//             "first_name",
	//             "last_name",
	//             "email",
	//             "phone_number",
	//             "job_title",
	//             "role",
	//             "disable",
	//             "is_active"
	//         ];

	//         // Build payload with only allowed fields
	//         const payload: Record<string, any> = {};
	//         allowedFields.forEach((key) => {
	//             if (key in data) payload[key] = data[key];
	//         });

	//         try {
	//             const response = await this.http.patch(
	//                 config.api.endpoints.admin.modifyUser(userId),
	//                 payload
	//             );

	//             info({
	//                 title: "User modify",
	//                 description: "User successfully updated."
	//             });

	//             return response.data;
	//         } catch (error) {
	//             console.error("❌ modifyUser failed:", error);
	//             throw error; // propagate error to handleSubmit
	//         }
	//     };
	// }

	updateUserStatus(userId: string, status: boolean) {
		return async (dispatch: Dispatch<any>) => {
			try {
				const response = await this.http.patch(config.api.endpoints.admin.modifyUser(userId), {
					disable: status
				});

				info({
					title: "User Status",
					description: `User status successfully changed.`
				});
			} catch (error) {
				this.handleError(error, "Update User Status");
			}
		};
	}

	selectProduct(product: IProduct) {
		return (dispatch: Dispatch<any>) => {
			dispatch(selectProductAction(product));
		};
	}

	getEmailSchedule() {
		return async (dispatch: Dispatch<any>) => {
			try {
				const response = await this.http.get(config.api.endpoints.admin.email);
				dispatch(fetchEmailScheduleResponse(response.data[0]));
			} catch (error) {
				this.handleError(error, "Email Schedule");
			}
		};
	}

	updateEmailSchedule(schedule: IEmailSchedule) {
		return async (dispatch: Dispatch<any>) => {
			try {
				const response = await this.http.post<IEmailSchedule>(config.api.endpoints.admin.email, {
					...schedule,
					day: parseInt(schedule.day as any, 10)
				});

				dispatch(fetchEmailScheduleResponse(response.data));
				notification.info("Email schedule updated successfully");
			} catch (error) {
				this.handleError(error, "Email schedule");
			}
		};
	}

	updateCompanySubscription(subscriptionType: string, companyId: string) {
		return async (dispatch: Dispatch<any>) => {
			try {
				const response = await this.http.patch(
					config.api.endpoints.admin.updateCompanySubscription(companyId),
					{
						subscription_type: subscriptionType
					}
				);
				info({
					title: "Company Subscription",
					description: `Subscription successfully changed to ${subscriptionType}.`
				});
			} catch (error) {
				this.handleError(error, "Update Company Subscription");
			}
		};
	}
	removeUser(userId: string) {
		return async (dispatch: Dispatch<any>, getState: () => IApplicationState) => {
			try {
				const response = await this.http.delete(config.api.endpoints.admin.removeUser(userId));
				info("User successfully removed.");
			} catch (error) {
				this.handleError(error, "User Remove");
			}
		};
	}

	emailTest(email: string, name?: string, companyName?: string) {
		return async (dispatch: Dispatch<any>) => {
			try {
				let url = `${config.api.endpoints.admin.emailTest}?email=${email}`;
				if (name) {
					url += `&name=${name}`;
				}
				if (companyName) {
					url += `&company_name${companyName}`;
				}
				const response = await this.http.post<IEmailSchedule>(url);

				notification.info("Email send successfully");
			} catch (error) {
				this.handleError(error, "Email schedule");
			}
		};
	}

	//for guava

	getGuavaData() {
		return async (dispatch: Dispatch<any>) => {
			try {
				const response = await this.http.get(config.api.endpoints.admin.guava);
				return response.data;
			} catch (error) {
				this.handleError(error, "Guava Data");
				throw error;
			}
		};
	}

	getGuavaDataStg() {
		return async (dispatch: Dispatch<any>) => {
			try {
				const response = await this.http.get(config.api.endpoints.admin.guavastg);
				return response.data;
			} catch (error) {
				this.handleError(error, "Guava Data");
				throw error;
			}
		};
	}

	/**
	 * Fetches product statistics from the /products/statistics/ API endpoint
	 * Returns comprehensive statistics including:
	 * - Total products count
	 * - Products with/without ingredients
	 * - Products with/without scores (nutrition and sustainability)
	 * - Detailed score breakdowns
	 * 
	 * This replaces the need to fetch total products from dashboard API
	 * and provides more accurate, consistent product statistics.
	 */
	getProductStatistics() {
		return async (dispatch: Dispatch<any>) => {
			try {
				const response = await this.http.get(config.api.endpoints.admin.productStatistics);
				dispatch(productStatisticsResponseAction(response.data.data));
				return response.data;
			} catch (error) {
				this.handleError(error, "Product Statistics");
				throw error;
			}
		};
	}



}

export default new AdminService();
